import { FC, useCallback, useEffect, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import RefreshIcon from '@mui/icons-material/Refresh';
import TableGeneralModal from '../tables/tableInventory/TableGeneralModal';
import Orders from './order/Order';
import BasicCli from '../../main/components/basicCli/BasicCli';
import RestaurantTable from './restaurantTable/RestaurantTable';
import axiosInstance from '../../main/initializers/axios';
import { ApiConstants } from '../../main/constants/ApiConstants';
import ITable from '../../main/interfaces/ITable';
import useGetUser from '../../main/hooks/useGetUser';
import JwtManager from '../../main/utils/jwtManager';
import { useTranslation } from 'react-i18next';
import './Restaurant.css';
import {
  RESTAURANT_SELECTED_ROOM_KEY,
  RoomCustomizationDto,
  TableWithLayout,
  applyLayoutToTables,
  initTablePositions,
} from './restaurantLayoutShared';
import { prefetchRestaurantOrderData } from './order/restaurantOrderDataCache';

type RestaurantTableStatusChangedPayload = {
  companyId?: number;
  tableId?: number;
  isBusy?: boolean;
  usedById?: number | null;
};

const Restaurant: FC = () => {
  const [tables, setTables] = useState<TableWithLayout[]>([]);
  const [rooms, setRooms] = useState<RoomCustomizationDto[]>([]);
  const [selectedTables, setSelectedTables] = useState<number[]>([]);
  const [selectedTable, setSelectedTable] = useState<ITable | null>(null);
  const [selectedRoom, setSelectedRoom] = useState<RoomCustomizationDto | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [invoiceCreation, setInvoiceCreation] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadWarning, setLoadWarning] = useState('');

  const user = useGetUser();
  const { i18n } = useTranslation();
  const companyId = user?.companyId ?? user?.CompanyId;

  const loadRestaurantView = useCallback(async (roomId?: number | null) => {
    try {
      setIsRefreshing(true);
      setLoadWarning('');
      void prefetchRestaurantOrderData().catch((error) => console.error(error));
      const [tableResp, roomResp] = await Promise.all([axiosInstance.post(ApiConstants.tables.list), axiosInstance.get('/api/roomcustomization/list')]);
      const baseTables = initTablePositions((tableResp.data || []) as ITable[]);
      const roomList = (roomResp.data || []) as RoomCustomizationDto[];
      const roomToLoad =
        roomList.find((room) => room.id === roomId) ??
        roomList.find((room) => room.id === Number(localStorage.getItem(RESTAURANT_SELECTED_ROOM_KEY))) ??
        roomList.find((room) => room.isDefault) ??
        roomList[0] ??
        null;

      setRooms(roomList);

      if (!roomToLoad) {
        setTables(baseTables);
        setSelectedRoom(null);
        localStorage.removeItem(RESTAURANT_SELECTED_ROOM_KEY);
        return;
      }

      setSelectedRoom(roomToLoad);
      localStorage.setItem(RESTAURANT_SELECTED_ROOM_KEY, String(roomToLoad.id));

      try {
        const layoutResp = await axiosInstance.get(`/api/roomcustomization/layout?roomCustomizationId=${roomToLoad.id}`);
        setTables(applyLayoutToTables(baseTables, layoutResp.data || []));
      } catch (layoutError) {
        console.error(layoutError);
        setTables(baseTables);
        setLoadWarning(i18n.t('restaurant.layoutLoadFallback', { defaultValue: 'Saved room layout could not be loaded. Showing default table positions.' }));
      }
    } catch (error) {
      console.error(error);
      setTables([]);
      setSelectedRoom(null);
      setLoadWarning(i18n.t('restaurant.viewLoadError', { defaultValue: 'Restaurant view could not be loaded.' }));
    } finally {
      setIsRefreshing(false);
    }
  }, [i18n]);

  useEffect(() => {
    const storedRoomId = localStorage.getItem(RESTAURANT_SELECTED_ROOM_KEY);
    void loadRestaurantView(storedRoomId ? Number(storedRoomId) : null);
  }, [loadRestaurantView]);

  useEffect(() => {
    if (!companyId || !JwtManager.accessToken) return;

    let mounted = true;
    let retryStartId: ReturnType<typeof setTimeout> | null = null;
    let connection: signalR.HubConnection | null = new signalR.HubConnectionBuilder()
      .withUrl(`${process.env.REACT_APP_API_URL}hubs/restaurant-table`, {
        accessTokenFactory: () => JwtManager.accessToken ?? '',
        withCredentials: false,
      })
      .withAutomaticReconnect()
      .build();

    const applyTableStatus = (payload?: RestaurantTableStatusChangedPayload) => {
      if (!mounted || !payload?.tableId) return;
      if (payload.companyId && payload.companyId !== companyId) return;

      setTables((prev) =>
        prev.map((table) =>
          table.id === payload.tableId
            ? {
                ...table,
                isBusy: payload.isBusy,
                usedById: payload.usedById ?? null,
              }
            : table,
        ),
      );

      setSelectedTable((prev) =>
        prev?.id === payload.tableId
          ? {
              ...prev,
              isBusy: payload.isBusy,
              usedById: payload.usedById ?? null,
            }
          : prev,
      );
    };

    const scheduleStart = () => {
      if (!mounted) return;
      if (retryStartId) clearTimeout(retryStartId);
      retryStartId = setTimeout(() => {
        void startConnection();
      }, 5000);
    };

    const startConnection = async () => {
      if (!mounted || !connection) return;

      try {
        if (connection.state === signalR.HubConnectionState.Connected || connection.state === signalR.HubConnectionState.Connecting) {
          return;
        }

        await connection.start();
        await connection.invoke('JoinCompany', companyId);
      } catch (err) {
        if ((err as any)?.name !== 'AbortError') {
          console.error('Failed to start restaurant table hub', err);
        }
        scheduleStart();
      }
    };

    connection.on('restaurantTableStatusChanged', applyTableStatus);
    connection.onreconnected(async () => {
      if (!mounted || !connection) return;
      try {
        if (connection.state === signalR.HubConnectionState.Connected) {
          await connection.invoke('JoinCompany', companyId);
        }
        await loadRestaurantView(selectedRoom?.id);
      } catch {
      }
    });

    connection.onclose(() => {
      scheduleStart();
    });

    void startConnection();

    return () => {
      mounted = false;
      if (retryStartId) clearTimeout(retryStartId);
      if (!connection) return;

      const closingConnection = connection;
      connection = null;
      void (async () => {
        try {
          if (closingConnection.state === signalR.HubConnectionState.Connected) {
            await closingConnection.invoke('LeaveCompany', companyId);
          }
        } catch {
        }

        try {
          await closingConnection.stop();
        } catch {
        }
      })();
    };
  }, [companyId, loadRestaurantView, selectedRoom?.id]);

  const handleSelectTable = useCallback(
    (tableId: number) => {
      const table = tables.find((item) => item.id === tableId) ?? null;
      setSelectedTable(table);
      setSelectedTables([tableId]);
      setIsModalOpen(true);
    },
    [tables],
  );

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setInvoiceCreation(false);
    void loadRestaurantView(selectedRoom?.id);
  }, [loadRestaurantView, selectedRoom?.id]);

  const resolveStatus = useCallback(
    (table: TableWithLayout) => {
      const isLockedByAnotherUser = table.isBusy === true && table.usedById != null && table.usedById !== user?.id;
      if (isLockedByAnotherUser) return 'available-soon';
      if (table.isBusy) return 'booked';
      return 'available';
    },
    [user?.id],
  );

  return (
    <BasicCli>
      <div className="restPage">
        <div className=" restCard">
          <div className="rest-wrapper">
            <div className="rest-header">
              <div className="rest-header-info">
                <span className="rest-title">{i18n.t('menu.Restaurant', { defaultValue: 'Restaurant' })}</span>
              </div>

              <div className="rest-header-actions">
                <div className="rest-group">
                  <button
                    type="button"
                    className="ui-control rest-refresh-btn"
                    onClick={() => void loadRestaurantView(selectedRoom?.id)}
                    disabled={isRefreshing}
                    title={i18n.t('generic.refresh', { defaultValue: 'Refresh' })}
                    aria-label={i18n.t('generic.refresh', { defaultValue: 'Refresh' })}
                  >
                    <RefreshIcon fontSize="small" />
                  </button>
                  <select
                    className="ui-control ui-select rest-select"
                    value={selectedRoom?.id ?? ''}
                    onChange={(e) => void loadRestaurantView(e.target.value ? Number(e.target.value) : null)}
                  >
                    <option value="">{i18n.t('restaurant.selectRoom', { defaultValue: 'Select room' })}</option>
                    {rooms.map((room) => (
                      <option key={room.id} value={room.id}>
                        {room.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="restBody">
              <div className="rest-canvas">
                <div className="rest-canvas-surface">
                  {loadWarning ? <div className="rest-empty-state">{loadWarning}</div> : null}
                  {!loadWarning && tables.length === 0 ? (
                    <div className="rest-empty-state">
                      {i18n.t('restaurant.noTablesAvailable', { defaultValue: 'No tables are available to display.' })}
                    </div>
                  ) : null}
                  {tables.map((table) => {
                    const status = resolveStatus(table);

                    return (
                      <div
                        key={table.id}
                        className={`rest-table-wrapper ${selectedTables.includes(table.id!) ? 'rest-table-selected' : ''}`}
                        style={{ left: `${table.posX}%`, top: `${table.posY}%` }}
                      >
                        <RestaurantTable
                          tableId={table.id!}
                          table={table}
                          status={status}
                          timer={table.isBusy ? '5m 30s' : ''}
                          tableCode={table.code}
                          selected={selectedTables.includes(table.id!)}
                          onSelect={handleSelectTable}
                          isRounded={false}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <TableGeneralModal
          headerName={selectedTable?.code ?? i18n.t('tables.table', { defaultValue: 'Table' })}
          hideHeader
          hideFooter
          height="100%"
          radius="0px"
          width="100%"
          isFullScreen
          handleClose={closeModal}
          handleClick={() => setInvoiceCreation(true)}
        >
          <Orders
            tableIds={selectedTables}
            table={selectedTable?.code ?? i18n.t('tables.table', { defaultValue: 'Table' })}
            selectedTable={selectedTable ?? undefined}
            createOrUpdate={invoiceCreation}
            onClick={() => setInvoiceCreation(false)}
            handleClose={closeModal}
          />
        </TableGeneralModal>
      )}
    </BasicCli>
  );
};

export default Restaurant;
